import { FC, useEffect } from "react";
import { 
  Box, Card, CardContent, 
  Grid, Paper, Stack, 
  Typography, useMediaQuery, useTheme 
} from "@mui/material";
import { useForm, FormProvider } from "react-hook-form"
import { useContracts } from "../../hooks/useContracts";
import { useTranslation } from "react-i18next";
import { CouponDetail } from "@/components/form-fields/CouponField/types/coupons";
import CouponField from "@/components/form-fields/CouponField/CouponField";
import useContractCoupon from "../../hooks/useContractCoupon";
import { RichHTML } from "@gisce/oficina-virtual-components";
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';

const CouponLikeDetail: FC<{promocode: CouponDetail}> = ({promocode}) => {
  // Component inspirat per 
  // https://dribbble.com/shots/12090093-DailyUI-061-Redeem-Coupon

  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));

  return <Box
    sx={{
      filter: `drop-shadow(0 0 0.15rem gray)`,
      padding: {xs: 1, sm: 2},
    }}
    >
      <Stack direction="row">
        {upSm && <Box 
          minWidth="75px"
          minHeight="125px"
          sx={{
            borderTopLeftRadius: theme.custom?.constants?.borderRadius,
            borderBottomLeftRadius: theme.custom?.constants?.borderRadius,
            borderRight: "2px dashed grey",
            background: "white",
            mask: `
              radial-gradient(circle at top right, transparent 15px, white 16px),
              radial-gradient(circle at bottom right, transparent 15px, white 16px)`,
            maskComposite: "intersect",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "12px",
            paddingRight: "20px"
          }}
        >
          <Box
            sx={{
              backgroundImage: `url(/images/barcode.png)`,
              backgroundSize: "175% 175%",
              backgroundRepeat: "repeat",
              backgroundPosition: `0 ${Math.floor(Math.random() * 201)}px`,
              borderRadius: "5px",
              height:"100%",
              width: "100%"
            }}
          >
          </Box>
        </Box>}
        <Box
          p={2}
          sx={{
            borderTopRightRadius: theme.custom?.constants?.borderRadius,
            borderBottomRightRadius: theme.custom?.constants?.borderRadius,
            background: "white",
            mask: `${
              ` radial-gradient(circle at top left, transparent 15px, white 16px),
                radial-gradient(circle at bottom left, transparent 15px, white 16px)`
              + (!upSm ? 
                `
                  , radial-gradient(circle at top right, transparent 15px, white 16px),
                  radial-gradient(circle at bottom right, transparent 15px, white 16px)
                  ` 
              : '')
            }`,
            maskComposite: "intersect",
          }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Typography variant="h6">{promocode.code}</Typography>
          <Box flexGrow={1}>
            <RichHTML html={promocode.description} />
          </Box>
          <Typography textAlign="right" variant="button">
            {promocode.createDate}
          </Typography>
        </Box>
      </Stack>
  </Box>
}


const CouponSimpleDetail: FC<{promocode: CouponDetail}> = ({promocode}) => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  return <Paper
    elevation={4}
    sx={{
      display: "flex",
      flexDirection: downSm ? "column" : "row",
      justifyContent: "space-between",
      padding: 1,
      marginBottom: 2,
      width: "100%"
    }}
  >
    {downSm ?
      <>
        <Typography variant="h6">{promocode.code}</Typography>
        <Typography textAlign="left" variant="button">
          {promocode.createDate}
        </Typography>
      </>
    : 
      <Box
        display={"flex"}
        flexDirection={"column"}
        padding={1}
      >
        <Typography variant="h6">{promocode.code}</Typography>
        <Typography textAlign="left" variant="button">
          {promocode.createDate}
        </Typography>
      </Box>
    }
    <Box flexGrow={1}>
      <RichHTML html={promocode.description} />
    </Box>
  </Paper>
}


interface CouponsListPanelProps {
  applyCoupon: boolean;
}

const CouponsDetailsPanel: FC<CouponsListPanelProps> = ({
  applyCoupon
}) => {
  const { t } = useTranslation();
  const { 
    currentContract
  } = useContracts("electricity");

  const couponsSchema = z.object({
    coupons: z.string().array()
  });

  type CouponsSchemaType = z.infer<typeof couponsSchema>;

  const methods = useForm<CouponsSchemaType>({
    resolver: zodResolver(couponsSchema),
    defaultValues: {
      coupons: []
    }
  });

  const {
    watch
  } = methods;

  const { handleApplyCoupon } = useContractCoupon({
    association: {id: currentContract.id, type: "polissa"}, type: "electricity"
  });

  const watchedCoupons = watch('coupons');

  useEffect(() => {
    const newCoupon = watchedCoupons[0];
    if (newCoupon) {
      handleApplyCoupon(newCoupon);
    }
  }, [watchedCoupons, handleApplyCoupon])

  const appliedPromocodes = currentContract.promocodes;

  return (
    <Card id="coupon-list">
      <CardContent>
        <Typography variant="h5" mb={1}>
          { t('common:text.contracts_promocodes_title') } ({appliedPromocodes.length})
        </Typography>
        {applyCoupon && <FormProvider {...methods}>
          <form>
            <CouponField
              multiple={false}
              fieldName="coupons"
              submitButtonLabel={t("common:text.contractation_promotional_apply")}
              association={{
                id: currentContract.id, 
                type: "polissa"
              }}
              usedCodes={currentContract.promocodes.map(couponDetail => couponDetail.code)}
              type={"electricity"}
            />
          </form>
        </FormProvider>}
        <Grid container>
            {appliedPromocodes.length > 0 &&
              appliedPromocodes.map((promocode, idx) => (
                <Grid item key={idx} width={"100%"}>
                  <CouponSimpleDetail promocode={promocode}/>
                </Grid>
              )) 
            } 
            
        </Grid>
      </CardContent>
    </Card>
  )
}


export default CouponsDetailsPanel;